"use client"

import * as React from "react"
import { UploadSimpleIcon, type Icon } from "@phosphor-icons/react"
import { cn } from "cn"

import { Button } from "./button"

/** Makes any element a drop target for files; spread `dropProps` on it and style it with `dragging`. */
function useFileDrop(onFiles: (files: File[]) => void, { multiple = true }: { multiple?: boolean } = {}) {
  const [dragging, setDragging] = React.useState(false)
  const depth = React.useRef(0)

  const dropProps = {
    onDragEnter: (event: React.DragEvent) => {
      if (!event.dataTransfer.types.includes("Files")) return
      event.preventDefault()
      depth.current += 1
      setDragging(true)
    },
    onDragOver: (event: React.DragEvent) => {
      if (event.dataTransfer.types.includes("Files")) event.preventDefault()
    },
    onDragLeave: () => {
      depth.current = Math.max(0, depth.current - 1)
      if (depth.current === 0) setDragging(false)
    },
    onDrop: (event: React.DragEvent) => {
      event.preventDefault()
      event.stopPropagation()
      depth.current = 0
      setDragging(false)
      const files = Array.from(event.dataTransfer.files)
      if (files.length > 0) onFiles(multiple ? files : files.slice(0, 1))
    },
  }

  return { dragging, dropProps }
}

function FileDropZone({
  title,
  description,
  actionLabel = "Choose Files",
  actionIcon: ActionIcon,
  actionVariant,
  accept,
  multiple = true,
  footer,
  secondaryAction,
  size = "default",
  onFiles,
  className,
  ...props
}: Omit<React.ComponentProps<"div">, "title"> & {
  title: React.ReactNode
  description?: React.ReactNode
  actionLabel?: string
  actionIcon?: Icon
  /** outline when the view's one primary action lives elsewhere, e.g. a footer's Continue. */
  actionVariant?: "default" | "outline"
  accept?: string
  multiple?: boolean
  /** A line under the button, e.g. another way to start. */
  footer?: React.ReactNode
  /** Beside the button, e.g. another kind of thing to add. */
  secondaryAction?: React.ReactNode
  /** compact: one row, for adding more files under a list. */
  size?: "default" | "compact"
  onFiles: (files: File[]) => void
}) {
  const input = React.useRef<HTMLInputElement>(null)
  const { dragging, dropProps } = useFileDrop(onFiles, { multiple })

  return (
    <div
      data-slot="file-drop-zone"
      data-dragging={dragging || undefined}
      data-size={size}
      {...dropProps}
      className={cn(
        "group/drop flex flex-col items-center justify-center rounded-2xl border border-dashed border-foreground/15 bg-canvas/75 px-6 py-14 text-center backdrop-blur-xl backdrop-saturate-150 transition-colors data-dragging:border-primary data-dragging:bg-primary/5 data-[size=compact]:flex-row data-[size=compact]:justify-start data-[size=compact]:px-4 data-[size=compact]:py-[11px] data-[size=compact]:text-start data-[size=compact]:gap-3",
        className
      )}
      {...props}
    >
      {size === "compact" ? (
        <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-background text-muted-foreground ring-1 ring-foreground/10 transition-colors group-data-dragging/drop:bg-primary group-data-dragging/drop:text-primary-foreground">
          <UploadSimpleIcon className="size-[18px]" aria-hidden="true" />
        </span>
      ) : (
        <UploadSimpleIcon
          weight="light"
          aria-hidden="true"
          className="mb-4 size-10 shrink-0 text-muted-foreground/80 transition-[color,translate] group-data-dragging/drop:-translate-y-1 group-data-dragging/drop:text-primary-text"
        />
      )}
      <div className="flex max-w-xl min-w-0 flex-col gap-2 group-data-[size=compact]/drop:max-w-none group-data-[size=compact]/drop:flex-1 group-data-[size=compact]/drop:flex-row group-data-[size=compact]/drop:items-baseline group-data-[size=compact]/drop:gap-2">
        <p className="typo-h5 text-foreground group-data-[size=compact]/drop:typo-body-md">{title}</p>
        {description && <p className="typo-body-md text-balance text-muted-foreground group-data-[size=compact]/drop:typo-body-sm group-data-[size=compact]/drop:truncate">{description}</p>}
      </div>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-3 group-data-[size=compact]/drop:mt-0 group-data-[size=compact]/drop:gap-2">
        <Button
          shape="pill"
          variant={actionVariant ?? (size === "compact" ? "outline" : "default")}
          size={size === "compact" ? "sm" : "lg"}
          onClick={() => input.current?.click()}
        >
          {ActionIcon && <ActionIcon data-icon="inline-start" />}
          {actionLabel}
        </Button>
        {secondaryAction}
      </div>
      {footer && <p className="typo-caption mt-4 text-muted-foreground group-data-[size=compact]/drop:mt-0">{footer}</p>}
      <input
        ref={input}
        type="file"
        accept={accept}
        multiple={multiple}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => {
          const files = Array.from(event.target.files ?? [])
          event.target.value = ""
          if (files.length > 0) onFiles(files)
        }}
      />
    </div>
  )
}

export { FileDropZone, useFileDrop }
